import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicProspectCopy, publicProspectPageConfig } from '../../config/publicProspectPageConfig';
import { resolvePublicCampaignMailingAccess } from '../../services/public/publicMailingAccessService';
import { createPublicResponseOperationId } from '../../services/public/publicMailingResponseRequest';
import { submitPublicCampaignMailingResponse } from '../../services/public/publicMailingResponseService';
import {
  createEmptyPublicProspectForm,
  freezePublicResponseAttempt,
  hasValidationErrors,
  validatePublicProspectForm,
} from './publicOwnerResponseForm';
import {
  createEmptyValuationDraft,
  applyConstructionYearTextEdit,
  projectUsableImportedConstructionYear,
  reducePublicValuationConstructionYearLifecycle,
  validateValuationBuildingStep,
  validateValuationFinancialStep,
  buildPropertyValueV3Response,
} from './publicProspectValuation.js';
import {
  completeOwnedDirtyPreflight,
  createClearedSensitivePublicDrafts,
  createDirtyPreflightOwner,
  createReceivedPublicResponseState,
  ownsLatestResolver,
  ownsPublicResponseGeneration,
  releaseOwnedInFlight,
  shouldDisablePublicProspectControls,
  transferDirtyPreflightToInFlight,
  tryAcquireDirtyPreflightLock,
} from './publicOwnerResponseState';
import PublicProspectHeader from './PublicProspectHeader';
import PropertyHero from './PropertyHero';
import ObjectiveSelector from './ObjectiveSelector';
import PublicProspectForm from './PublicProspectForm';
import PublicProspectValuationForm from './PublicProspectValuationForm.jsx';
import PublicProspectContactWidget from './PublicProspectContactWidget';
import PublicProspectConfirmation from './PublicProspectConfirmation';
import './publicProspectFonts.css';

const detectLanguage = () =>
  typeof navigator !== 'undefined' && String(navigator.language).toLowerCase().startsWith('en')
    ? 'en'
    : 'fr';
const errorCode = (error) => String(error?.code || error?.details?.reason || '');

let nextPageGeneration = 0;

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setDesktop(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return desktop;
}

function PublicMailingLandingGeneration({ token }) {
  const [generation] = useState(() => ++nextPageGeneration);
  const generationOwner = useRef({ token, generation });
  const mounted = useRef(true);
  const latestResolverId = useRef(0);
  const inFlight = useRef(null);
  const preflightLock = useRef(null);
  const statusRef = useRef('loading');
  const attemptDirtyRef = useRef(false);
  const errorSummary = useRef(null);
  const isDesktop = useIsDesktop();

  const [language, setLanguage] = useState(detectLanguage);
  const [status, setStatus] = useState('loading');
  const [property, setProperty] = useState(null);
  const [receivedIntents, setReceivedIntents] = useState([]);
  const [form, setForm] = useState(() => createEmptyPublicProspectForm(detectLanguage()));
  const [valuation, setValuation] = useState(() => createEmptyValuationDraft());
  const [valuationStep, setValuationStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [attempt, setAttempt] = useState(null);
  const [attemptDirty, setAttemptDirty] = useState(false);
  const [message, setMessage] = useState('');
  const [step, setStep] = useState('hero');
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [lastSubmittedIntent, setLastSubmittedIntent] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [preflightPending, setPreflightPending] = useState(false);

  const setStatusTracked = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const setAttemptDirtyTracked = useCallback((next) => {
    attemptDirtyRef.current = next;
    setAttemptDirty(next);
  }, []);

  const copy = getPublicProspectCopy(language);
  const confirmation = publicProspectPageConfig.confirmation[language];
  const importedNumberOfUnits =
    Number.isInteger(property?.numberOfUnits) && property.numberOfUnits > 0
      ? property.numberOfUnits
      : null;
  const importedConstructionYear = projectUsableImportedConstructionYear(property?.constructionYear);

  const applyClearedSensitiveDrafts = useCallback(
    (languageOverride = language) => {
      const cleared = createClearedSensitivePublicDrafts(languageOverride);
      setForm(cleared.form);
      setValuation(cleared.valuation);
      setValuationStep(cleared.valuationStep);
      setErrors(cleared.errors);
      setMessage(cleared.message);
      setAttempt(cleared.attempt);
      setAttemptDirtyTracked(cleared.attemptDirty);
      setSelectedIntent(cleared.selectedIntent);
    },
    [language, setAttemptDirtyTracked],
  );

  const enterTerminalAccess = useCallback(
    (terminalStatus) => {
      applyClearedSensitiveDrafts();
      setStatusTracked(terminalStatus);
    },
    [applyClearedSensitiveDrafts, setStatusTracked],
  );

  const finishSuccess = useCallback((owner, safeProperty, intents, intent) => {
    if (!mounted.current || !ownsPublicResponseGeneration(owner || generationOwner.current, generationOwner.current)) {
      return false;
    }
    if (owner && inFlight.current !== owner) return false;
    if (owner) inFlight.current = releaseOwnedInFlight(inFlight.current, owner);
    const received = createReceivedPublicResponseState(safeProperty, intents, intent);
    setAttempt(received.attempt);
    setAttemptDirtyTracked(received.attemptDirty);
    setForm(createEmptyPublicProspectForm(language));
    setValuation(createEmptyValuationDraft());
    setValuationStep(1);
    setErrors(received.errors);
    setMessage(received.message);
    setProperty(received.property);
    setReceivedIntents(received.receivedIntents);
    setSelectedIntent(null);
    setLastSubmittedIntent(intent);
    setStep('confirmation');
    setStatusTracked('ready');
    return true;
  }, [language, setAttemptDirtyTracked, setStatusTracked]);

  const resolve = useCallback(
    async (submissionOwner = null) => {
      const resolverOwner = { ...generationOwner.current, requestId: ++latestResolverId.current };
      try {
        const data = await resolvePublicCampaignMailingAccess(token);
        if (
          !mounted.current ||
          !ownsLatestResolver(resolverOwner, generationOwner.current, latestResolverId.current)
        ) {
          return null;
        }
        const intents = Array.isArray(data?.receivedIntents) ? data.receivedIntents : [];
        if (!data?.property) {
          // During submission reconciliation, do not clear yet — caller decides terminal vs retry.
          if (submissionOwner) return { terminalStatus: 'expired' };
          enterTerminalAccess('expired');
          return null;
        }
        setProperty(data.property);
        setValuation((current) =>
          reducePublicValuationConstructionYearLifecycle(
            {
              property: data.property,
              valuation: current,
              valuationStep: 1,
              selectedIntent: null,
            },
            { type: 'resolve_access', property: data.property },
          ).valuation,
        );
        setReceivedIntents(intents);
        setStatusTracked('ready');
        if (submissionOwner && intents.includes(submissionOwner.intent)) {
          finishSuccess(submissionOwner, data.property, intents, submissionOwner.intent);
        }
        return data;
      } catch (error) {
        if (
          !mounted.current ||
          !ownsLatestResolver(resolverOwner, generationOwner.current, latestResolverId.current)
        ) {
          return null;
        }
        const code = errorCode(error);
        const terminal =
          code.includes('not-found') || code === 'public_access_unavailable' ? 'expired' : 'unavailable';
        if (submissionOwner) {
          // Authoritative access loss only — transient resolve failures stay non-terminal here
          // so the caller can preserve drafts for retry when the original submit was uncertain.
          if (terminal === 'expired') return { terminalStatus: 'expired' };
          return { resolveFailed: true };
        }
        enterTerminalAccess(terminal);
        return null;
      }
    },
    [enterTerminalAccess, finishSuccess, setStatusTracked, token],
  );

  useEffect(() => {
    mounted.current = true;
    setStatusTracked('loading');
    resolve();
    return () => {
      mounted.current = false;
    };
  }, [resolve, setStatusTracked]);

  useEffect(() => {
    setForm((current) => ({ ...current, language }));
  }, [language]);

  const markDirty = () => {
    setErrors({});
    setMessage('');
    if (attempt) {
      setAttempt(null);
      setAttemptDirtyTracked(true);
    }
  };

  const change = (path, value) => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    setForm((current) => {
      const next = structuredClone(current);
      let cursor = next;
      for (let index = 0; index < path.length - 1; index += 1) cursor = cursor[path[index]];
      cursor[path[path.length - 1]] = value;
      return next;
    });
    markDirty();
  };

  const changeValuation = (key, value) => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    setValuation((current) => {
      if (key === 'constructionYearText') {
        return applyConstructionYearTextEdit(current, value);
      }
      const next = { ...current, [key]: value };
      if (key === 'constructionYearUnknown') {
        next.constructionYearHintInitialized = true;
      }
      return next;
    });
    markDirty();
  };

  const patchValuation = (next) => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    // Patches from applyConstructionYearUnknown already mark the draft initialized.
    setValuation(next);
    markDirty();
  };

  const sendAttempt = async (currentAttempt, { releasePreflightOwner = null } = {}) => {
    if (inFlight.current) return;
    if (releasePreflightOwner) {
      const submissionOwner = {
        ...generationOwner.current,
        attempt: currentAttempt,
        intent: currentAttempt.formResponse.intent,
      };
      const transfer = transferDirtyPreflightToInFlight({
        lockRef: preflightLock,
        inFlightRef: inFlight,
        preflightOwner: releasePreflightOwner,
        submissionOwner,
      });
      if (!transfer.transferred) return;
      setPreflightPending(false);
    } else {
      if (preflightLock.current) return;
      const submissionOwner = {
        ...generationOwner.current,
        attempt: currentAttempt,
        intent: currentAttempt.formResponse.intent,
      };
      inFlight.current = submissionOwner;
    }
    const submissionOwner = inFlight.current;
    setStatusTracked('submitting');
    setMessage(copy.submitting);
    try {
      const receipt = await submitPublicCampaignMailingResponse({
        token,
        operationId: currentAttempt.operationId,
        formResponse: currentAttempt.formResponse,
      });
      if (!mounted.current || inFlight.current !== submissionOwner) return;
      if (receipt.status === 'received' || receipt.duplicate) {
        const nextIntents = receivedIntents.includes(receipt.intent)
          ? receivedIntents
          : [...receivedIntents, receipt.intent];
        finishSuccess(submissionOwner, property, nextIntents, receipt.intent);
        return;
      }
      throw new Error('unexpected receipt');
    } catch (error) {
      if (!mounted.current || inFlight.current !== submissionOwner) return;
      const code = errorCode(error);
      const resolved = await resolve(submissionOwner);
      if (!mounted.current || (inFlight.current !== submissionOwner && !resolved)) return;
      if (resolved?.receivedIntents?.includes(submissionOwner.intent)) return;
      inFlight.current = releaseOwnedInFlight(inFlight.current, submissionOwner);
      if (code.includes('failed-precondition')) {
        enterTerminalAccess('unavailable');
        return;
      }
      if (
        code.includes('not-found') ||
        code === 'public_access_unavailable' ||
        resolved?.terminalStatus === 'expired'
      ) {
        enterTerminalAccess('expired');
        return;
      }
      // Uncertain / retryable failure: preserve frozen attempt and visible draft.
      setStatusTracked('retry');
      setMessage(copy.retryMessage);
    }
  };

  /**
   * Shared dirty-attempt ownership gate for valuation V3 and sale V2.
   * Acquires a synchronous preflight lock before awaiting resolve().
   */
  const runDirtyAttemptPreflight = async (intent) => {
    if (inFlight.current || preflightLock.current) {
      return { outcome: 'blocked', preflightOwner: null };
    }
    if (!attemptDirtyRef.current) {
      return { outcome: 'continue', preflightOwner: null };
    }
    const owner = createDirtyPreflightOwner({
      token: generationOwner.current.token,
      generation: generationOwner.current.generation,
      intent,
    });
    if (!tryAcquireDirtyPreflightLock(preflightLock, owner, inFlight)) {
      return { outcome: 'blocked', preflightOwner: null };
    }
    setPreflightPending(true);
    const resolved = await resolve();
    const completion = completeOwnedDirtyPreflight({
      owner,
      generationOwner: generationOwner.current,
      lockRef: preflightLock,
      mounted: mounted.current,
      resolved,
      intent,
      status: statusRef.current,
    });
    if (completion.outcome === 'stale' || completion.outcome === 'abort') {
      setPreflightPending(completion.preflightPending);
      // Never replace an expired/unavailable terminal status set by resolve().
      return { outcome: completion.outcome, preflightOwner: null };
    }
    if (completion.outcome === 'already_received') {
      setPreflightPending(false);
      finishSuccess(null, completion.resolved.property, completion.resolved.receivedIntents, intent);
      return { outcome: 'already_received', preflightOwner: null };
    }
    setAttemptDirtyTracked(false);
    return { outcome: 'continue', preflightOwner: completion.preflightOwner };
  };

  const focusErrors = () => {
    requestAnimationFrame(() => errorSummary.current?.focus());
  };

  const advanceValuationStep = () => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    if (valuationStep === 1) {
      const validation = validateValuationFinancialStep(valuation, { language });
      if (hasValidationErrors(validation)) {
        setErrors(validation);
        setMessage(copy.errorSummary);
        focusErrors();
        return;
      }
      setErrors({});
      setValuationStep(2);
      return;
    }
    if (valuationStep === 2) {
      const validation = validateValuationBuildingStep(valuation, { importedNumberOfUnits });
      if (hasValidationErrors(validation)) {
        setErrors(validation);
        setMessage(copy.errorSummary);
        focusErrors();
        return;
      }
      setErrors({});
      setValuationStep(3);
    }
  };

  const submitValuation = async (event) => {
    event.preventDefault();
    if (inFlight.current || preflightLock.current) return;
    const built = buildPropertyValueV3Response({
      form: { ...form, importedNumberOfUnitsHint: importedNumberOfUnits },
      valuation,
      language,
    });
    if (built.errors) {
      setAttempt(null);
      setErrors(built.errors);
      setMessage(copy.errorSummary);
      focusErrors();
      return;
    }
    // Snapshot at the explicit submit action — preserved across the dirty preflight await.
    const validatedSnapshot = structuredClone(built.response);
    const preflight = await runDirtyAttemptPreflight('property_value');
    if (preflight.outcome !== 'continue') return;
    const currentAttempt =
      attempt || freezePublicResponseAttempt(createPublicResponseOperationId(), validatedSnapshot);
    if (!attempt) setAttempt(currentAttempt);
    await sendAttempt(currentAttempt, { releasePreflightOwner: preflight.preflightOwner });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current || preflightLock.current) return;
    const draft = {
      ...form,
      intent: selectedIntent,
      language,
      schemaVersion: 2,
    };
    const validation = validatePublicProspectForm(draft);
    if (hasValidationErrors(validation)) {
      setAttempt(null);
      setErrors(validation);
      setMessage(copy.errorSummary);
      focusErrors();
      return;
    }
    const validatedSnapshot = structuredClone(draft);
    const preflight = await runDirtyAttemptPreflight(selectedIntent);
    if (preflight.outcome !== 'continue') return;
    const currentAttempt =
      attempt ||
      freezePublicResponseAttempt(createPublicResponseOperationId(), validatedSnapshot);
    if (!attempt) setAttempt(currentAttempt);
    await sendAttempt(currentAttempt, { releasePreflightOwner: preflight.preflightOwner });
  };

  const selectIntent = (intent) => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    if (receivedIntents.includes(intent)) return;
    setSelectedIntent(intent);
    setForm((current) => ({
      ...current,
      intent,
      ownershipAttested: false,
      consentAccepted: false,
    }));
    // Fresh intent draft via shared lifecycle helper (same path the regression tests exercise).
    // Must initialize here — resolve-only prefill is wiped by a bare empty draft reset.
    const next = reducePublicValuationConstructionYearLifecycle(
      {
        property,
        valuation,
        valuationStep,
        selectedIntent: intent,
      },
      { type: 'select_intent', intent },
    );
    setValuation(next.valuation);
    setValuationStep(next.valuationStep);
    setErrors({});
    setStep('form');
  };

  const changeLanguage = (nextLanguage) => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    setLanguage(nextLanguage);
  };

  const goBackFromForm = () => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    setSelectedIntent(null);
    setStep('objectives');
    setErrors({});
  };

  const goBackFromValuation = () => {
    if (shouldDisablePublicProspectControls({ status: statusRef.current, preflightPending })) return;
    if (valuationStep > 1) {
      setValuationStep((current) => current - 1);
      setErrors({});
      return;
    }
    goBackFromForm();
  };

  const disabled = shouldDisablePublicProspectControls({ status, preflightPending });
  const showShell = ['ready', 'submitting', 'retry'].includes(status) && property;
  const desktopShowHero = true;
  const isValuation = selectedIntent === 'property_value';

  const liveStep = useMemo(() => {
    if (!showShell) return null;
    if (status === 'loading') return null;
    if (step === 'confirmation') return 'confirmation';
    if (step === 'form' && selectedIntent) return 'form';
    if (step === 'objectives' || (isDesktop && step !== 'hero')) return 'objectives';
    return 'hero';
  }, [isDesktop, selectedIntent, showShell, status, step]);

  return (
    <div className="prospect-page min-h-screen bg-prospect-cream px-3 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-3 font-prospect sm:px-6 sm:py-8">
      <div className="mx-auto w-full max-w-6xl overflow-x-hidden">
        <PublicProspectHeader
          language={language}
          onLanguageChange={changeLanguage}
          disabled={disabled}
          copy={copy}
        />
        <main className="mt-4 rounded-2xl bg-prospect-cream p-4 sm:p-8">
          <div aria-live="polite" className="sr-only">
            {message}
          </div>
          {status === 'loading' ? (
            <p className="prospect-ui text-center text-[#1f4a52]">{copy.loading}</p>
          ) : null}
          {status === 'expired' || status === 'unavailable' ? (
            <div className="space-y-3 text-center">
              <h1 className="prospect-display text-[1.9rem] font-semibold leading-[1.08] tracking-tight text-[#0B1F2A]">
                {status === 'expired' ? copy.expiredTitle : copy.unavailableTitle}
              </h1>
              <p className="prospect-ui text-[1.0625rem] leading-relaxed text-[#1f4a52]">
                {status === 'expired' ? copy.expiredBody : copy.unavailableBody}
              </p>
            </div>
          ) : null}
          {showShell ? (
            <div className="space-y-8">
              {!isDesktop && liveStep !== 'hero' ? (
                <p className="prospect-ui text-sm font-medium text-[#1f4a52]">
                  {copy.stepLabel(
                    liveStep === 'objectives' ? 1 : liveStep === 'form' ? 2 : 3,
                    3,
                  )}
                </p>
              ) : null}

              {(isDesktop && desktopShowHero) || liveStep === 'hero' ? (
                <PropertyHero
                  property={property}
                  copy={copy}
                  language={language}
                  disabled={disabled}
                  compact={!isDesktop}
                  onDiscover={() => setStep('objectives')}
                />
              ) : null}

              {isDesktop ? <div className="border-t border-gray-200" aria-hidden="true" /> : null}

              {(isDesktop || liveStep === 'objectives') && liveStep !== 'form' && liveStep !== 'confirmation' ? (
                <ObjectiveSelector
                  language={language}
                  copy={copy}
                  confirmation={confirmation}
                  receivedIntents={receivedIntents}
                  onSelect={selectIntent}
                  disabled={disabled}
                />
              ) : null}

              {(isDesktop && selectedIntent && liveStep === 'form') || (!isDesktop && liveStep === 'form') ? (
                isValuation ? (
                  <PublicProspectValuationForm
                    language={language}
                    copy={copy}
                    form={form}
                    valuation={valuation}
                    valuationStep={valuationStep}
                    errors={errors}
                    message={message}
                    status={status}
                    disabled={disabled}
                    importedNumberOfUnits={importedNumberOfUnits}
                    importedConstructionYear={importedConstructionYear}
                    onValuationChange={changeValuation}
                    onValuationPatch={patchValuation}
                    onContactChange={(field, value) => change(['contact', field], value)}
                    onConsentChange={(value) => change(['consentAccepted'], value)}
                    onSubmit={submitValuation}
                    onBack={goBackFromValuation}
                    onNext={advanceValuationStep}
                    onRefresh={() => resolve()}
                    errorSummaryRef={errorSummary}
                  />
                ) : (
                  <PublicProspectForm
                    intent={selectedIntent}
                    language={language}
                    copy={copy}
                    form={form}
                    errors={errors}
                    message={message}
                    status={status}
                    disabled={disabled}
                    onChange={change}
                    onSubmit={submit}
                    onBack={goBackFromForm}
                    onRefresh={() => resolve()}
                    errorSummaryRef={errorSummary}
                  />
                )
              ) : null}

              {liveStep === 'confirmation' ? (
                <PublicProspectConfirmation
                  language={language}
                  property={property}
                  intent={lastSubmittedIntent}
                  onContinue={() => {
                    setStep('objectives');
                    setLastSubmittedIntent(null);
                  }}
                />
              ) : null}

              <p className="prospect-ui text-sm text-[#1f4a52]">
                <span className="font-medium">{copy.incorrectProperty}</span> {copy.incorrectPropertyHint}
              </p>
            </div>
          ) : null}
        </main>
      </div>
      <PublicProspectContactWidget
        open={contactOpen}
        onOpenChange={setContactOpen}
        copy={copy}
      />
    </div>
  );
}

export default function PublicMailingLandingPage() {
  const { token } = useParams();
  return <PublicMailingLandingGeneration key={token} token={token} />;
}
